import React, { useState, useEffect } from 'react';
import { Tabs, Tab, Button, Box, Paper, Typography, Grid } from '@mui/material';
import { useQueryClient } from 'react-query';
import Header from '../Header';
import FindCustomerWrapper from './FindCustomerWrapper';
import QuickSignupWrapper from './QuickSignupWrapper';
import QuickEditWrapper from './QuickEditWrapper';
import ApplyCouponWrapper from './ApplyCouponWrapper';
import useCustomer from '../../Utils/UseCustomer';
import useServiceUrls from '../../Utils/UseServiceUrls';
import { getQueryString, mapServiceUrls, onSnippetComplete } from '../../Helpers/Globals';
import useDeviceProfile from '../../Utils/UseDeviceProfile';

export default function QuickLinkSnippet(props) {
    const [sessionKey, setSessionKey] = useState('');
    const [loginDomain, setLoginDomain] = useState('');
    const [cartUid, setCartUid] = useState('');
    const [cartExternalId, setCartExternalId] = useState('');
    const [cartSourceExternalId, setCartSourceExternalId] = useState('');
    const [returnUrl, setReturnUrl] = useState('');

    const [customerUid, setCustomerUid] = useState('');
    const [customer, setCustomer] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [redeemedCouponCode, setRedeemedCouponCode] = useState('');
    const queryClient = useQueryClient();

    const { serviceUrlsStatus, serviceUrls, serviceUrlsError } = useServiceUrls(loginDomain);
    const { status: customerStatus, customer: customerData, error: customerError } = useCustomer(serviceUrls, sessionKey, customerUid)
    const {status, deviceProfile, error} = useDeviceProfile(mapServiceUrls(serviceUrls), sessionKey);

    useEffect(() => {
        setSessionKey(getQueryString(window.location.href, 'bl_sk') ?? getQueryString(window.location.href,'SessionKey') ?? '');
        setLoginDomain(getQueryString(window.location.href, 'bl_LoginDomain') ?? getQueryString(window.location.href,'LoginDomain') ?? '');
        setReturnUrl(getQueryString(window.location.href, 'bl_rurl') ?? getQueryString(window.location.href,'ReturnUrl') ?? '');
        setCartUid(getQueryString(window.location.href, 'bL_CartUid') ?? getQueryString(window.location.href,'CartUid') ?? '');
        setCartExternalId(getQueryString(window.location.href, 'bl_CartExternalId') ?? getQueryString(window.location.href,'cartExternalId') ?? '')
        setCartSourceExternalId(getQueryString(window.location.href, 'bl_CartSourceExternalId') ?? getQueryString(window.location.href,'CartSourceExternalId') ?? '')
    }, [window.location.search])

    useEffect(() => {
        if (customerData) {
            setCustomer(customerData);
        }
    }, [customerData])

    useEffect(() => {
        if (customerError) {
            console.error(customerError);
        }
    }, [customerError])

    const addCustomer = (customer) => {
        if (customer) {
            setCustomerUid(customer.Uid);
            setCustomer(customer);
        }
    }

    const handleCustomerSaved = () => {
      
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleClearCustomer = () => {
        setCustomerUid('');
        setCustomer(null);
        setRedeemedCouponCode('');
    };

    const handleCouponRedeemed = (couponCode) => {
        if (couponCode) {
            setRedeemedCouponCode(couponCode);
        }
    };


    const renderTabContent = () => {
        switch (activeTab) {
            case 0:
                return <FindCustomerWrapper customer={customer} setCustomer={addCustomer} onTabChange={handleTabChange} />;
            case 1:
                return <QuickSignupWrapper customer={customer} setCustomer={addCustomer} onTabChange={handleTabChange} />;
            case 2:
                return <QuickEditWrapper customer={customer} customerUid={customerUid} setCustomer={addCustomer} onCustomerSaved={handleCustomerSaved} />;
            case 3:
                return <ApplyCouponWrapper customer={customer} customerUid={customerUid} setCustomer={addCustomer} onCouponRedeemed={handleCouponRedeemed} />;
            default:
                return null;
        }
    };

    const getCustomerDisplayName = () => {
        if (!customer) return '';
        const parts = [];
        if (customer.FirstName) parts.push(customer.FirstName);
        if (customer.LastName) parts.push(customer.LastName);
        if (parts.length > 0) return parts.join(' ');
        if (customer.CompanyName) return customer.CompanyName;
        return 'Customer';
    };

    return (
        <div className={'container'} style={{
            maxWidth: '100%',
            width: '100%'
        }}>
            <Header title={'QuickLink'} />
            {customer && (
                <Paper 
                    elevation={2} 
                    sx={{ 
                        padding: '10px 15px', 
                        marginBottom: '12px',
                        backgroundColor: '#f9f9f9',
                        border: '1px solid #ddd',
                        borderRadius: '8px'
                    }}
                >
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '15px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body1" sx={{ marginRight: '8px' }}>
                                {getCustomerDisplayName() || 'N/A'}
                            </Typography>
                            {customer.Code && (
                                <Typography variant="body2" sx={{ color: '#666' }}>
                                    (#{customer.Code})
                                </Typography>
                            )}
                        </Box>
                        {customer.EmailAddress && (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body1">
                                    {customer.EmailAddress}
                                </Typography>
                            </Box>
                        )}
                        {(customer.MobilePhone || customer.Phone1) && (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body1">
                                    {customer.MobilePhone || customer.Phone1 || 'N/A'}
                                </Typography>
                            </Box>
                        )}
                        {customer.LoyaltyPointsAccounts && customer.LoyaltyPointsAccounts.map((account, index) => (
                            <Box key={account.Uid || index} sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ color: '#666', marginRight: '6px' }}>
                                    {account.Label || account.AccountClass?.Label || 'Points'}:
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    {account.Balance?.toLocaleString() || '0'}
                                </Typography>
                            </Box>
                        ))}
                        {customer.LoyaltyCurrencyAccounts && customer.LoyaltyCurrencyAccounts.map((account, index) => (
                            <Box key={account.Uid || index} sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ color: '#666', marginRight: '6px' }}>
                                    {account.Label || account.AccountClass?.Label || 'Currency'}:
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    ${account.Balance?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Paper>
            )}
            <div style={{ marginBottom: '15px' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Tabs 
                        value={activeTab} 
                        onChange={handleTabChange} 
                        aria-label="quick link tabs"
                        sx={{
                            '& .MuiTab-root': {
                                minWidth: '120px',
                                padding: '12px 16px',
                                textTransform: 'none',
                                fontSize: '14px',
                                fontWeight: 500,
                            },
                            '& .MuiTab-root:hover': {
                                color: '#619700',
                            },
                            '& .MuiTab-root.Mui-selected': {
                                color: '#619700 !important',
                            },
                            '& .MuiTabs-indicator': {
                                backgroundColor: '#619700',
                            }
                        }}
                    >
                        <Tab label="Find Customer" />
                        <Tab label="Quick Signup" />
                        <Tab label="Quick Edit" />
                        <Tab label="Apply Coupon" />
                    </Tabs>
                    <Box sx={{ display: 'flex', gap: '8px', marginRight: '15px' }}>
                        <Button
                            variant="outlined"
                            onClick={handleClearCustomer}
                            disabled={!customer}
                            sx={{ 
                                minWidth: '120px',
                                borderColor: '#393939',
                                color: '#393939',
                                '&:hover': {
                                    borderColor: '#393939',
                                    backgroundColor: 'rgba(57, 57, 57, 0.04)'
                                },
                                '&.Mui-disabled': {
                                    borderColor: 'rgba(0, 0, 0, 0.26)',
                                    color: 'rgba(0, 0, 0, 0.26)'
                                }
                            }}
                        >
                            Clear Customer
                        </Button>
                    </Box>
                </Box>
            </div>
            <div className={'snippetContainer center'}>
                {renderTabContent()}
            </div>
        </div>
    );
}

